import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useCycleMutations, useProjectCycles } from '@/lib/db/rxdb/hooks/entities/cycles'
import { useProjectCyclesEnabled } from '@/hooks/useProjectCyclesEnabled'
import useLoginStore from '@/store/LoginStore'

interface CreateCycleDialogProps {
  projectId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

function addDaysToDateString(dateString: string, days: number): string {
  const [y, m, d] = dateString.split('-').map(Number)
  if (!y || !m || !d) return ''
  const result = new Date(y, m - 1, d + days)
  const yyyy = result.getFullYear()
  const mm = String(result.getMonth() + 1).padStart(2, '0')
  const dd = String(result.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

function formatDateLabel(dateString: string): string {
  const [y, m, d] = dateString.split('-').map(Number)
  if (!y || !m || !d) return ''
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

export function CreateCycleDialog({ projectId, open, onOpenChange }: CreateCycleDialogProps) {
  const [name, setName] = useState('')
  const [startDate, setStartDate] = useState('')
  const [manualEndDate, setManualEndDate] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  const { createCycle } = useCycleMutations()
  const { cycles } = useProjectCycles(projectId)
  const { cycleDurationWeeks } = useProjectCyclesEnabled(projectId)
  const workspaceId = useLoginStore((state) => state.workspaceId)

  const derivedEndDate = useMemo(() => {
    if (!startDate || !cycleDurationWeeks) return ''
    return addDaysToDateString(startDate, cycleDurationWeeks * 7)
  }, [startDate, cycleDurationWeeks])

  const endDate = cycleDurationWeeks ? derivedEndDate : manualEndDate

  const handleCreate = async () => {
    if (!name.trim() || !startDate || !endDate || !workspaceId) return
    setIsCreating(true)
    try {
      const maxNumber = cycles?.reduce((max, c) => Math.max(max, c.number), 0) ?? 0
      await createCycle({
        envId: workspaceId,
        projectId,
        name: name.trim(),
        number: maxNumber + 1,
        startDate: new Date(startDate).getTime(),
        endDate: new Date(endDate).getTime(),
        status: 'draft',
        description: null,
        completedOn: null,
        completedBy: null,
      })
      toast.success('Cycle created')
      onOpenChange(false)
      setName('')
      setStartDate('')
      setManualEndDate('')
    } catch {
      toast.error('Failed to create cycle')
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create Cycle</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="cycle-name" className="text-sm">Name</Label>
            <Input
              id="cycle-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Sprint 1"
              className="mt-1.5"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="cycle-start" className="text-sm">Start date</Label>
              <Input
                id="cycle-start"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="mt-1.5"
              />
            </div>
            {cycleDurationWeeks ? (
              <div>
                <Label className="text-sm">End date</Label>
                <p className="text-sm text-muted-foreground mt-3">
                  {derivedEndDate
                    ? formatDateLabel(derivedEndDate)
                    : `Auto (${cycleDurationWeeks} week${cycleDurationWeeks > 1 ? 's' : ''})`}
                </p>
              </div>
            ) : (
              <div>
                <Label htmlFor="cycle-end" className="text-sm">End date</Label>
                <Input
                  id="cycle-end"
                  type="date"
                  value={manualEndDate}
                  onChange={(e) => setManualEndDate(e.target.value)}
                  className="mt-1.5"
                />
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={!name.trim() || !startDate || !endDate || isCreating}
          >
            {isCreating ? 'Creating...' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
